import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Users,
  Calendar,
  School,
  Shield,
  Star,
  Quote,
  ExternalLink,
  CheckCircle,
  Award,
  Heart,
  TrendingUp,
  Target,
  Building2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for metrics
const ICON_MAP = {
  users: Users,
  calendar: Calendar,
  school: School,
  shield: Shield,
  star: Star,
  award: Award,
  heart: Heart,
  trending: TrendingUp,
  target: Target,
  building: Building2,
};

const SocialProofSection = ({ config }) => {
  if (!config) return null;

  const {
    section_heading = 'Trusted by Families & Schools',
    section_subheading = 'Join thousands of swimmers building confidence and safety skills',
    metrics = [],
    testimonials = [],
    logos_heading = 'Trusted by Schools & Stakeholders',
    partner_logos = [],
    google_review_url = '',
    google_review_button_text = 'Read Reviews on Google',
  } = config;

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50" data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Social Proof</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {section_heading}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {section_subheading}
          </p>
        </div>

        {/* Trust Metrics Strip */}
        {metrics.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-16">
            {metrics.map((metric, idx) => {
              const IconComponent = ICON_MAP[metric.icon] || Shield;
              return (
                <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">{metric.value}</p>
                    <p className="text-sm text-slate-600">{metric.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Testimonials */}
        {testimonials.length > 0 && (
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.slice(0, 4).map((testimonial, idx) => (
                <Card key={testimonial.id || idx} className="border-0 shadow-lg hover:shadow-xl transition-all bg-white overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Headshot or Placeholder */}
                      <div className="flex-shrink-0">
                        {testimonial.headshot_url ? (
                          <img
                            src={getImageUrl(testimonial.headshot_url)}
                            alt={testimonial.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-slate-100"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-cyan-500/20 flex items-center justify-center">
                            <span className="text-lg font-bold text-primary">
                              {testimonial.name?.charAt(0) || 'A'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Quote className="w-4 h-4 text-primary/40" />
                          {testimonial.verified && (
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified {testimonial.role === 'School Admin' ? 'School' : 'Parent'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-700 mb-3 italic leading-relaxed">
                          "{testimonial.quote}"
                        </p>
                        <div className="flex flex-wrap items-center gap-x-2 text-sm">
                          <span className="font-semibold text-slate-900">{testimonial.name}</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-600">{testimonial.role}</span>
                          {testimonial.school && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-500">{testimonial.school}</span>
                            </>
                          )}
                        </div>
                        {testimonial.location && (
                          <p className="text-xs text-slate-400 mt-1">{testimonial.location}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Partner Logos */}
        {partner_logos.length > 0 && (
          <div className="mb-12">
            <p className="text-center text-sm font-medium text-slate-500 uppercase tracking-wider mb-8">
              {logos_heading}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-12">
              {partner_logos.map((logo, idx) => (
                <div
                  key={logo.id || idx}
                  className="grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={getImageUrl(logo.url)}
                    alt={logo.alt_text || 'Partner logo'}
                    className="h-10 lg:h-12 w-auto max-w-[120px] object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Google Reviews CTA */}
        {google_review_url && (
          <div className="text-center pt-8 border-t border-slate-200">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-slate-600 mb-4">See what others are saying about us</p>
            <a
              href={google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <Button variant="outline" className="border-slate-300 hover:border-primary hover:text-primary">
                {google_review_button_text}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialProofSection;
